/**
 * bos_cart-expiry.js
 * -----------------------------------------------------------------------------
 * Automatically empties the customer's basket once it has been sitting for a
 * configured number of hours.
 *
 * How it works
 *   • The moment the cart goes from empty → non-empty we stamp "now" into
 *     localStorage (the expiry clock starts here). Adding more items later does
 *     NOT push the deadline back — the basket has a hard lifespan from the first
 *     item so an abandoned configuration can't linger forever.
 *   • On every page load (and again live via a timer while the tab stays open)
 *     we compare the stamp against the threshold. When the timer hits zero the
 *     script calls Shopify's AJAX Cart API — POST /cart/clear.js — to empty the
 *     basket, then reloads so every server-rendered surface (cart bubble,
 *     mini-cart, cart page) reflects the empty state.
 *   • Emptying the basket by hand clears the stamp, so the clock restarts fresh
 *     next time something is added.
 *
 * Config (set on the <script> tag by snippets/bos_cart-expiry.liquid):
 *   data-expiry-hours   Lifespan in hours before the basket is cleared (>0).
 *
 * No dependencies — plain Cart AJAX API + localStorage.
 * -----------------------------------------------------------------------------
 */
(function () {
  "use strict";

  var script = document.currentScript;
  var STORAGE_KEY = "bos:cart-expiry:startedAt";

  // Resolve the configured lifespan; fall back to 72h if misconfigured.
  var hours = parseFloat(script && script.dataset.expiryHours);
  if (!isFinite(hours) || hours <= 0) hours = 72;
  var TTL_MS = hours * 60 * 60 * 1000;

  // Shopify exposes cart routes under this root (handles sub-folder locales).
  var root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";

  var liveTimer = null;

  function readStamp() {
    var raw;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null; // storage blocked (private mode etc.) — nothing to enforce
    }
    var ts = parseInt(raw, 10);
    return isFinite(ts) ? ts : null;
  }

  function writeStamp(ts) {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(ts));
    } catch (e) {
      /* ignore */
    }
  }

  function clearStamp() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function now() {
    return Date.now();
  }

  function getCart() {
    return fetch(root + "cart.js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    }).then(function (res) {
      return res.json();
    });
  }

  function clearCart() {
    return fetch(root + "cart/clear.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
    });
  }

  // Empty the basket, drop the stamp, and refresh the UI.
  function expire() {
    if (liveTimer) {
      clearTimeout(liveTimer);
      liveTimer = null;
    }
    return clearCart()
      .then(function () {
        clearStamp();
        // Reload so the server-rendered cart surfaces reflect the empty cart.
        window.location.reload();
      })
      .catch(function () {
        /* network error — leave the cart; we'll retry on the next load */
      });
  }

  // Schedule a live expiry while the tab stays open, so a long-lived session is
  // cleared on time without needing a fresh page load.
  function scheduleLive(msRemaining) {
    if (liveTimer) clearTimeout(liveTimer);
    // setTimeout caps out around 24.8 days; our TTL is well under that.
    liveTimer = setTimeout(expire, Math.max(0, msRemaining));
  }

  // Reconcile localStorage stamp against the actual cart, then enforce/schedule.
  function sync() {
    getCart()
      .then(function (cart) {
        var empty = !cart || cart.item_count === 0;

        if (empty) {
          // Nothing to expire — reset the clock for next time.
          clearStamp();
          if (liveTimer) {
            clearTimeout(liveTimer);
            liveTimer = null;
          }
          return;
        }

        var startedAt = readStamp();
        if (startedAt === null) {
          // Cart just became non-empty (or first visit with an existing cart):
          // start the clock now.
          startedAt = now();
          writeStamp(startedAt);
        }

        var elapsed = now() - startedAt;
        if (elapsed >= TTL_MS) {
          expire();
        } else {
          scheduleLive(TTL_MS - elapsed);
        }
      })
      .catch(function () {
        /* couldn't read the cart — try again next load */
      });
  }

  // Re-check when the tab regains focus: the deadline may have passed while it
  // was backgrounded, or the cart may have changed in another tab.
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") sync();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", sync);
  } else {
    sync();
  }
})();
